import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { payrollRunsAPI, usePayrollRun, usePayslips, usePayslip, useGeneratePayroll, useFinalizePayroll, useReopenPayroll, useDeletePayrollRun } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';
import { ArrowLeft, Play, CheckCircle, RefreshCw, Download, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { downloadBlob } from '@/utils/download';
import { extractPaginatedData } from '@/utils/pagination';

type PayrollRunStatus = 'draft' | 'processing' | 'completed' | 'finalized' | 'cancelled' | 'generating' | 'generated' | 'paid';

const statusConfig: Record<PayrollRunStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'bg-gray-100 text-gray-700' },
  processing: { label: 'Расчёт...', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Готово', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Отменено', color: 'bg-red-100 text-red-700' },
  generating: { label: 'Расчёт...', color: 'bg-yellow-100 text-yellow-700' },
  generated: { label: 'Готово', color: 'bg-blue-100 text-blue-700' },
  finalized: { label: 'Завершено', color: 'bg-green-100 text-green-700' },
  paid: { label: 'Оплачено', color: 'bg-emerald-100 text-emerald-700' },
};

export default function PayrollRunDetailPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canRunPayroll = can('run_payroll');
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);

  const { data: runData, isLoading: runLoading, refetch: refetchRun } = usePayrollRun(runId);
  const { data: payslipsData, isLoading: payslipsLoading, refetch: refetchPayslips } = usePayslips(runId);

  const generateMutation = useGeneratePayroll();
  const finalizeMutation = useFinalizePayroll();
  const reopenMutation = useReopenPayroll();
  const deleteMutation = useDeletePayrollRun();

  const [selectedPayslipId, setSelectedPayslipId] = useState<number | null>(null);

  const run = runData?.data || runData;
  const { items: payslips } = extractPaginatedData<any>(payslipsData, 1, 1000);
  const status = (run?.status as PayrollRunStatus) || 'draft';

  // Poll while generating
  useEffect(() => {
    if (status === 'processing' || status === 'generating') {
      const interval = setInterval(() => {
        refetchRun();
        refetchPayslips();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [status, refetchRun, refetchPayslips]);

  const handleGenerate = async () => {
    if (window.confirm('Создать расчёт штрафов для всех сотрудников?')) {
      await generateMutation.mutateAsync(runId);
      refetchRun();
    }
  };

  const handleFinalize = async () => {
    if (window.confirm('Завершить этот расчёт?')) {
      await finalizeMutation.mutateAsync(runId);
      refetchRun();
    }
  };

  const handleReopen = async () => {
    if (window.confirm('Открыть этот расчёт заново?')) {
      await reopenMutation.mutateAsync(runId);
      refetchRun();
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Удалить этот расчёт штрафов?')) {
      await deleteMutation.mutateAsync(runId);
      navigate('/payroll/runs');
    }
  };

  const handleExport = async () => {
    const blob = await payrollRunsAPI.export(runId);

    downloadBlob(blob, `penalty-report-${runId}.xlsx`);
  };

  if (runLoading || payslipsLoading) {
    return <LoadingSpinner />;
  }

  if (!run) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Расчёт не найден</p>
        <Link to="/payroll/runs" className="text-blue-600 hover:underline">
          Вернуться к списку
        </Link>
      </div>
    );
  }

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <div className="space-y-6 page-shell">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4 sm:items-center">
          <Link to="/payroll/runs">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{run.name}</h1>
            <p className="text-gray-500">
              {run.branch?.name} • {run.period_start} — {run.period_end}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
            {config.label}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Количество сотрудников</p>
            <p className="text-2xl font-bold">{payslips.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Итого штрафов</p>
            <p className="text-2xl font-bold text-red-600">
              {Number(run.total_deductions || 0).toLocaleString('ru-RU')} сум
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Status</p>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
              {config.label}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {canRunPayroll && status === 'draft' && (
          <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
            <Play className="w-4 h-4 mr-2" />
            {generateMutation.isPending ? 'Расчёт...' : 'Рассчитать штрафы'}
          </Button>
        )}
        {canRunPayroll && (status === 'completed' || status === 'generated') && (
          <Button onClick={handleFinalize} disabled={finalizeMutation.isPending}>
            <CheckCircle className="w-4 h-4 mr-2" />
            {finalizeMutation.isPending ? 'Завершение...' : 'Завершить'}
          </Button>
        )}
        {canRunPayroll && (status === 'draft' || status === 'processing' || status === 'generating') && (
          <Button variant="outline" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Удаление...' : 'Удалить'}
          </Button>
        )}
        {canRunPayroll && status === 'finalized' && (
          <Button variant="outline" onClick={handleReopen} disabled={reopenMutation.isPending}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Открыть заново
          </Button>
        )}
        <Button variant="outline" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Скачать Excel
        </Button>
      </div>

      {/* Penalty Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle>Отчёт по штрафам сотрудников</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold">Отдел</th>
                  <th className="text-right py-3 px-4 font-semibold">Рабочие дни</th>
                  <th className="text-right py-3 px-4 font-semibold">Дни опозданий</th>
                  <th className="text-right py-3 px-4 font-semibold">Дни неявки</th>
                  <th className="text-right py-3 px-4 font-semibold">Штраф за опоздание</th>
                  <th className="text-right py-3 px-4 font-semibold">Штраф за неявку</th>
                  <th className="text-right py-3 px-4 font-semibold">Другие штрафы</th>
                  <th className="text-right py-3 px-4 font-semibold text-red-700">Итого штрафов</th>
                  <th className="text-center py-3 px-4 font-semibold">Детали</th>
                </tr>
              </thead>
              <tbody>
                {payslips.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-10 text-gray-500">
                      {status === 'draft'
                        ? 'Нажмите «Рассчитать штрафы», чтобы создать расчёт'
                        : 'Данные не найдены'}
                    </td>
                  </tr>
                ) : (
                  payslips.map((payslip: any) => (
                    <tr key={payslip.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium">{payslip.employee?.full_name}</div>
                        <div className="text-sm text-gray-500">{payslip.employee?.employee_id}</div>
                      </td>
                      <td className="py-3 px-4">{payslip.employee?.department?.name || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        {payslip.days_worked || 0}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-yellow-600">
                        {payslip.days_late || 0}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-orange-600">
                        {payslip.days_absent || 0}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-red-500">
                        {Number(payslip.late_deductions || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-red-500">
                        {Number(payslip.absence_deductions || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-red-500">
                        {Number(payslip.other_deductions || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-red-700">
                        {Number(payslip.total_deductions || 0).toLocaleString()} сум
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPayslipId(payslip.id)}
                        >
                          Смотреть
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Penalty Detail Drawer */}
      {selectedPayslipId && (
        <PenaltyDetailDrawer
          payslipId={selectedPayslipId}
          onClose={() => setSelectedPayslipId(null)}
        />
      )}
    </div>
  );
}

interface PenaltyDetailDrawerProps {
  payslipId: number;
  onClose: () => void;
}

function PenaltyDetailDrawer({ payslipId, onClose }: PenaltyDetailDrawerProps) {
  const { data: payslipData, isLoading } = usePayslip(payslipId);

  const payslip = payslipData?.data || payslipData;
  const breakdown = payslip?.breakdown || {};
  const penaltiesByType = breakdown?.penalties_by_type || {};
  const summary = breakdown?.summary || {};
  const attendance = breakdown?.attendance || {};

  const typeLabels: Record<string, string> = {
    late: 'Опоздание',
    absent: 'Отсутствие',
    no_show: 'Неявка',
    early_leave: 'Ранний уход',
    no_checkout: 'Нет отметки ухода',
    no_checkin: 'Нет отметки прихода',
    manual: 'Ручной штраф',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/50 sm:items-stretch" onClick={onClose}>
      <div
        className="h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-4 shadow-xl sm:h-full sm:w-[500px] sm:rounded-none sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold">Детали штрафов</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : !payslip ? (
          <p className="text-gray-500">Данные не найдены</p>
        ) : (
          <div className="space-y-6">
            {/* Employee Info */}
            <div>
              <h3 className="font-semibold text-lg">{payslip.employee?.full_name}</h3>
              <p className="text-gray-500">{payslip.employee?.department?.name}</p>
              <p className="text-sm text-gray-400">ID: {payslip.employee?.employee_id}</p>
            </div>

            {/* Attendance Summary */}
            <div className="border rounded-lg p-4 space-y-2">
              <h4 className="font-semibold text-sm text-gray-600 uppercase">Посещаемость</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span>Рабочие дни:</span>
                  <span className="font-mono font-bold">{payslip.days_worked || attendance.days_present || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Опоздания:</span>
                  <span className="font-mono font-bold text-yellow-600">{payslip.days_late || attendance.days_late || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Неявки:</span>
                  <span className="font-mono font-bold text-red-600">{payslip.days_absent || attendance.days_absent || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Часы:</span>
                  <span className="font-mono font-bold">{payslip.hours_worked || attendance.hours_worked || 0}</span>
                </div>
              </div>
            </div>

            {/* Penalty Breakdown by Type */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-sm text-gray-600 uppercase">Штрафы по типам</h4>
              {Object.keys(penaltiesByType).length === 0 ? (
                <p className="text-sm text-gray-400">Штрафов нет</p>
              ) : (
                Object.entries(penaltiesByType).map(([type, data]: [string, any]) => (
                  <div key={type} className="flex justify-between items-center">
                    <div>
                      <span className="text-sm">{typeLabels[type] || type}</span>
                      <span className="text-xs text-gray-400 ml-2">({data.count} раз)</span>
                    </div>
                    <span className="font-mono text-red-600 font-semibold">
                      {Number(data.amount).toLocaleString()} сум
                    </span>
                  </div>
                ))
              )}
              <hr />
              <div className="flex justify-between font-bold text-lg">
                <span>Итого штрафов</span>
                <span className="font-mono text-red-700">
                  {Number(payslip.total_deductions || 0).toLocaleString()} сум
                </span>
              </div>
            </div>

            {/* Status Summary */}
            {summary.total_penalties > 0 && (
              <div className="border rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm text-gray-600 uppercase">Статусы штрафов</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-green-700">Подтверждено:</span>
                    <span className="font-mono">{Number(summary.approved_total || 0).toLocaleString()} сум</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Ожидает:</span>
                    <span className="font-mono">{Number(summary.pending_total || 0).toLocaleString()} сум</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Отклонено:</span>
                    <span className="font-mono">{Number(summary.rejected_total || 0).toLocaleString()} сум</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
